import { inject, Injectable } from '@angular/core';
import { Router, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, CanActivateFn } from '@angular/router';
import { AuthContext } from '@Core/interfaces/auth-context.interface';
import { AuthService } from '@Core/services/auth.service';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { LoggingService } from '@Services/logging.service';
import { UserService } from '@Services/user.service';
import { PermissionsShared } from '@Shared/Directives/sm-permissions-shared.class';
import { TenantPermissions } from '@Core/enums/codegen/tenant-permissions.enum';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthGuardService implements CanActivate {
    constructor(public auth: AuthService,
        public router: Router,
        private userService : UserService,
        private logger: LoggingService) { }

    canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
        return this.auth.authContext$.pipe(switchMap((authContext) => {
            const authenticated: boolean = this.auth.isAuthenticated(authContext);
            if (!authenticated) {
                this.logger.logInfo("User is not authenticated. Redirecting to the Login page.");
                return of(this.loginUrl());
            } else if (!this.auth.getTenantKey() && !state.url.startsWith("/Admin")) {
                this.logger.logInfo("The token did not have a tenant key. Redirecting to the Login page.");
                return of(this.loginUrl());
            } else if (state.url.startsWith("/Work") || state.url.startsWith("/Mgmt")) {
                if (!this.auth.getAreaKey()) {
                    let tree: UrlTree = this.router.createUrlTree(['SelectArea']);
                    return of(tree);
                } else {
                    return of(true);
                }
            } else if (state.url.startsWith("/Conf")) {
                return this.userService.Permissions.pipe(
                    take(1),
                    map(grantedPermissions => {
                        if (PermissionsShared.HasAny(grantedPermissions, [
                            TenantPermissions.Common_SystemAccess,
                            TenantPermissions.Common_Tenant,
                            TenantPermissions.Common_Programs])) {
                            return true;
                        }
                        this.logger.logInfo("User lacks authority to access Conf. Redirecting to Work.");
                        return this.workUrl();
                    })
                );
            } else if (state.url.startsWith('/Admin')) {
                if (this.isAdminToken(authContext)) {
                    return of(true);
                }
                this.logger.logInfo("Token is not a valid Admin token. Redirecting to Work.");
                return of(this.workUrl());
            } else {
                return of(true);
            }
        }));
    }

    private loginUrl(): UrlTree {
        return this.router.createUrlTree(['']);
    }

    private workUrl(): UrlTree {
        return this.router.createUrlTree(['/Work']);
    }

    private isAdminToken(authContext: AuthContext): boolean {
        return authContext.tenantKey == 'uwAdmin'; // This identifies a token of an admin user.
    }
}

// This should replace the above AuthGuardService, we require both a Tenant and an Area to be selected
export const requireTenantAndAreaSelection: CanActivateFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
    const auth = inject(AuthService);
    const router = inject(Router);
    const logger = inject(LoggingService);

    return auth.isAuthenticated$.pipe(switchMap((authenticated) => {
        if (!authenticated) {
            logger.logInfo("User is not authenticated. Redirecting to the Login page.");
            router.navigate(['']);
            return of(false);
        } else if (!auth.getTenantKey()) {
            logger.logInfo("The token did not have a tenant key. Redirecting to the Login page.");
            router.navigate(['']);
            return of(false);
        } else if (state.url.startsWith("/Work") || state.url.startsWith("/Mgmt")) {
            if (!auth.getAreaKey()) {
                let tree: UrlTree = router.createUrlTree(['SelectArea']);
                return of(tree);
            } else {
                return of(true);
            }
        } else {
            return of(true);
        }
    }));
};

// Use for places where we require a Tenant to be selected (but not an Area)
export const requireTenantSelection: CanActivateFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
    const auth = inject(AuthService);
    const router = inject(Router);
    const logger = inject(LoggingService);

    return auth.isAuthenticated$.pipe(switchMap((authenticated) => {
        if (!authenticated) {
            logger.logInfo("User is not authenticated. Redirecting to the Login page.");
            router.navigate(['']);
            return of(false);
        } else if (!auth.getTenantKey()) {
            logger.logInfo("The token did not have a tenant key. Redirecting to the Login page.");
            router.navigate(['']);
            return of(false);
        } else {
            return of(true);
        }
    }));
};

/** Guard that only allows access to /altlogin when altLoginEnabled is "true". */
export const canActivateAltLogin: CanActivateFn = () => {
    const router = inject(Router);

    if (environment.altLoginEnabled === 'true') {
        return true;
    }

    return router.createUrlTree(['']);
};

